import { useCallback, useEffect, useState } from 'react';
import {
  fetchReviewBatches,
  fetchReviewCandidates,
  sendToReview,
  type ReviewBatchesView,
  type ReviewCandidatesView,
  type ReviewSource,
  type SendToReviewResult,
} from '../lib/api';
import { useActionError } from '../lib/action-error';
import { ask } from '../lib/ask';
import { DocId } from '../lib/doc-id';
import { InfoTip } from '../lib/info-tip';
import { documentTypeLabel, labels } from '../lib/labels';
import { RoleNote, useGate } from '../lib/session';

type LoadState = { status: 'loading' } | { status: 'error' } | { status: 'ok'; view: ReviewCandidatesView };

/** ESC-20: how often the panel reads the batch manifests again while a sent batch has no project. */
export const BATCH_POLL_MS = 5_000;

/** Statuses after which a batch's project id will not appear any more (escalation's
 *  `TERMINAL_STATUSES`, `review-batch.ts`). */
const SETTLED = new Set(['in-review', 'results-fetched', 'failed', 'abandoned', 'deleted']);
/** The settled statuses where the batch did not end in a project in Data Studio: the job failed,
 *  the batch was released (`abandoned`), or its project was deleted. */
const ENDED_WITHOUT_PROJECT = new Set(['failed', 'abandoned', 'deleted']);

/**
 * QA-114: the Data Studio line of one sent batch. *Created* only once the manifest has the project
 * id and the batch did not fail, get released, or lose its project; *being created* while the batch
 * is still on its way; nothing once it settled without one.
 */
function projectLine(status: string | undefined, projectId: string | undefined): 'created' | 'pending' | null {
  if (ENDED_WITHOUT_PROJECT.has(status ?? '')) return null;
  if (projectId) return 'created';
  return SETTLED.has(status ?? '') ? null : 'pending';
}

/**
 * *Send to review*'s guard.
 * Opened from either row that names it
 * (`human-review-drain`, `spot-check-drain`) or the standing drain line — "the same control,
 * different source" (`condition-copy.ts`). Shows what it will act on, confirms, reports what
 * happened: this row's own uniform guard shape.
 *
 * ESC-20: escalation answers once the batch is staged, so the report shows each type's batch id and
 * status, then reads the batch manifests every `BATCH_POLL_MS` until each batch has its Datasaur
 * project id (or has settled). The panel also lists this source's stuck batches. A failed send shows
 * its error (`useActionError`).
 */
export function SendToReviewPanel({ source, onClose, onSent }: { source: ReviewSource; onClose: () => void; onSent: () => void }) {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<SendToReviewResult | null>(null);
  const [sending, setSending] = useState(false);
  const [batches, setBatches] = useState<ReviewBatchesView | null>(null);
  const action = useActionError();
  const gate = useGate('administer');

  // The batch list is a read beside the send: when it fails, the panel just shows no batches.
  const loadBatches = useCallback(() => {
    fetchReviewBatches()
      .then((view) => setBatches(Array.isArray(view?.batches) ? view : null))
      .catch(() => undefined);
  }, []);

  useEffect(() => loadBatches(), [source, loadBatches]);

  const batchOf = (batchId: string | null) =>
    batchId ? batches?.batches.find((batch) => batch.batchId === batchId) : undefined;

  // Until every batch this send made has its project (or settled), read the manifests again.
  const waiting =
    result?.results.some((group) => {
      if (!group.batchId || group.error || group.projectId) return false;
      const batch = batchOf(group.batchId);
      return !batch?.projectId && !SETTLED.has(batch?.status ?? '');
    }) ?? false;
  useEffect(() => {
    if (!waiting) return undefined;
    const timer = setTimeout(loadBatches, BATCH_POLL_MS);
    return () => clearTimeout(timer);
  }, [waiting, batches, loadBatches]);

  // QA-120: `stale` and `unfinished` are a warning, the rest is a plain line.
  const pollerLine = batches?.poller ? labels.sendToReview.pollerLine(batches.poller) : null;
  const pollerLate = batches?.poller?.state === 'stale' || batches?.poller?.state === 'unfinished';

  const stuck = (batches?.batches ?? []).filter((batch) => batch.stuck && batch.source === source);

  useEffect(() => {
    setState({ status: 'loading' });
    setResult(null);
    fetchReviewCandidates(source)
      .then((view) => {
        setState({ status: 'ok', view });
        setSelected(new Set(view.candidates.map((c) => c.docId))); // default select-all
      })
      .catch(() => setState({ status: 'error' }));
  }, [source]);

  function toggle(docId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  }

  function toggleAll(candidates: ReviewCandidatesView['candidates']) {
    setSelected((prev) => (prev.size === candidates.length ? new Set() : new Set(candidates.map((c) => c.docId))));
  }

  async function handleSend() {
    const docIds = [...selected];
    if (docIds.length === 0) return;
    const answers = await ask({
      ...labels.sendToReview.confirm(docIds.length),
      bodyTone: 'info',
      fields: [{ name: 'reason', label: labels.sendToReview.reasonLabel, required: true }],
      confirmLabel: labels.sendToReview.sendButton(docIds.length),
    });
    if (!answers) return;
    setSending(true);
    try {
      let sent: SendToReviewResult | undefined;
      if (await action.run(async () => (sent = await sendToReview(docIds, source, answers.reason)))) {
        setResult(sent!);
        loadBatches();
        onSent();
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="panel" aria-label={labels.sendToReview.heading(source)}>
      <div className="panel__head">
        <h3>{labels.sendToReview.heading(source)}</h3>
        {/* Close is navigation, not a write, so it is text — and it sits in the head rather than
            inside the heading, so the heading's accessible name stays the panel's name. */}
        <button type="button" className="btn btn--text" onClick={onClose}>
          {labels.sendToReview.close}
        </button>
      </div>

      {action.notice}
      {pollerLine &&
        (pollerLate ? (
          <p className="notice notice--warn">
            {pollerLine}
          </p>
        ) : (
          <p className="panel__sub">{pollerLine}</p>
        ))}
      {state.status === 'loading' && <p className="notice">{labels.sendToReview.loading}</p>}
      {state.status === 'error' && (
        <p className="notice notice--crit" role="alert">
          {labels.sendToReview.loadFailed}
        </p>
      )}

      {state.status === 'ok' && !result && (
        <>
          {state.view.candidates.length === 0 ? (
            <p className="panel__sub">{labels.sendToReview.empty}</p>
          ) : (
            <>
              {state.view.isFloor && <p className="notice notice--info">{labels.sendToReview.isFloorNote}</p>}
              <div className="responsive-table">
                <table>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        aria-label={labels.sendToReview.selectAll}
                        checked={selected.size === state.view.candidates.length && state.view.candidates.length > 0}
                        disabled={!gate.allowed}
                        onChange={() => toggleAll(state.view.candidates)}
                      />
                    </th>
                    <th>{labels.sendToReview.columns.document}</th>
                    <th>{labels.sendToReview.columns.type}</th>
                  </tr>
                </thead>
                <tbody>
                  {state.view.candidates.map((candidate) => (
                    <tr key={candidate.docId}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={candidate.docId}
                          checked={selected.has(candidate.docId)}
                          disabled={!gate.allowed}
                          onChange={() => toggle(candidate.docId)}
                        />
                      </td>
                      <td>
                        <a className="mono" href={`#doc/${candidate.docId}`} aria-label={candidate.docId}>
                          <DocId id={candidate.docId} />
                        </a>
                        {candidate.notSent && (
                          <div className="panel__note">
                            {labels.sendToReview.notSentNote(candidate.notSent.at, candidate.notSent.reason)}
                            {labels.sendToReview.skipTip(candidate.notSent.reason) && (
                              <InfoTip
                                label={labels.sendToReview.notSentLabel(candidate.docId)}
                                text={labels.sendToReview.skipTip(candidate.notSent.reason)}
                              />
                            )}
                          </div>
                        )}
                      </td>
                      <td>{candidate.type}</td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
              <div className="action-row flow-md">
                <RoleNote action="administer" />
                {/* The write this panel exists for: guarded, and the label carries the count so
                    the operator reads what it will act on off the button itself. */}
                <button
                  type="button"
                  className="btn btn--guard"
                  {...gate.props}
                  disabled={!gate.allowed || selected.size === 0 || sending}
                  onClick={handleSend}
                >
                  {labels.sendToReview.sendButton(selected.size)}
                </button>
              </div>
            </>
          )}
        </>
      )}

      {result && (
        <div className={result.results.some((group) => group.error) ? 'notice notice--crit' : 'notice notice--good'} role="status">
          <p>{labels.sendToReview.reported}</p>
          <ul className="report">
            {result.results.map((group) => {
              const batch = batchOf(group.batchId);
              const status = batch?.status ?? group.status;
              const projectId = batch?.projectId ?? group.projectId;
              const line = group.batchId && !group.error ? projectLine(status, projectId) : null;
              return (
                <li key={`${group.type}-${group.source}`}>
                  {labels.sendToReview.groupReport(
                    group.type,
                    group.documentCount,
                    group.batchId,
                    group.error,
                    status,
                    (group.skipped?.length ?? 0) > 0,
                  )}
                  {line && (
                    <>
                      {' '}
                      {labels.sendToReview.projectReport(line === 'created' ? projectId : undefined)}
                    </>
                  )}
                  {group.skipped && group.skipped.length > 0 && (
                    <ul className="report">
                      {group.skipped.map((entry) => (
                        <li key={entry.stem}>
                          {labels.sendToReview.notSent(entry.stem, entry.reason)}
                          {labels.sendToReview.skipTip(entry.reason, entry.detail) && (
                            <InfoTip
                              label={labels.sendToReview.notSentLabel(`${group.type}/${entry.stem}`)}
                              text={labels.sendToReview.skipTip(entry.reason, entry.detail)}
                            />
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {batches && stuck.length > 0 && (
        <div className="flow-md" aria-label={labels.sendToReview.stuckHeading} role="region">
          <h4>{labels.sendToReview.stuckHeading}</h4>
          <p className="panel__sub">{labels.sendToReview.stuckNote(Math.round(batches.stuckAfterMs / 60_000))}</p>
          <div className="responsive-table">
            <table>
              <thead>
                <tr>
                  <th>{labels.sendToReview.stuckColumns.type}</th>
                  <th>{labels.sendToReview.stuckColumns.batch}</th>
                  <th>{labels.sendToReview.stuckColumns.status}</th>
                  <th>{labels.sendToReview.stuckColumns.age}</th>
                </tr>
              </thead>
              <tbody>
                {stuck.map((batch) => (
                  <tr key={batch.batchId}>
                    <td>{documentTypeLabel(batch.type)}</td>
                    <td className="mono">{batch.batchId}</td>
                    <td>{labels.sendToReview.stuckStatus(batch.status, batch.stuckReason)}</td>
                    <td>{labels.sendToReview.age(batch.ageMs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
